// Room chat (F-COM-1, design side panel "In-call messages"). Session-only
// history — stated in the UI. Rendering is escape-by-default (plain JSX text
// nodes; §9.2 bans dangerouslySetInnerHTML repo-wide). Only THIS panel
// subscribes to the chat slice: an incoming message re-renders the chat panel
// and nothing else (§9.3.7b).

import { ChangeEvent, FormEvent, memo, useCallback, useEffect, useRef, useState } from 'react';

import { engine } from '../engine';
import { probeRender } from '../perf/renderProbe';
import { clearChatUnread, useCallStore } from '../store/callStore';
import { IconButton } from '../ui/IconButton';
import { IconChevronLeft, IconClose, IconSend } from '../ui/icons';

function msgTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export const ChatPanel = memo(function ChatPanel({
  onClose,
  title,
}: {
  onClose: () => void;
  title?: string | null;
}) {
  probeRender('ChatPanel');
  const messages = useCallStore((s) => s.chat);
  const localIdentity = useCallStore((s) => s.localIdentity);
  const peopleCount = useCallStore((s) => s.order.length);
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // The panel only mounts while it is open, so any message seen here is read.
  useEffect(() => clearChatUnread(), [messages.length]);

  const onDraft = useCallback((e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value), []);
  const send = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const text = draft.trim();
      if (!text) return;
      setDraft('');
      void engine.sendChat(text);
    },
    [draft],
  );

  return (
    <div className="bz-panel bz-chat-panel" role="log" aria-label="Meeting chat">
      <div className="bz-panel-header">
        <IconButton shape="flat" label="Back" className="bz-mobile-only" onClick={onClose}>
          <IconChevronLeft size={17} />
        </IconButton>
        <div className="bz-rowmain">
          <span>In-call messages</span>
          <div className="bz-chat-subtitle bz-mobile-only">
            {title ?? 'Meeting'} · {peopleCount} {peopleCount === 1 ? 'person' : 'people'}
          </div>
        </div>
        <div className="bz-panel-header-actions bz-hide-mobile">
          <IconButton shape="flat" label="Close" onClick={onClose}>
            <IconClose size={14} />
          </IconButton>
        </div>
      </div>
      <p className="bz-chat-note">Messages are end-to-end encrypted and vanish when you leave.</p>
      <div ref={listRef} className="bz-chat-list">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`bz-mchat-msg${m.fromIdentity === localIdentity ? ' bz-chat-own' : ''}`}
          >
            <div className="bz-chat-fromrow">
              <span className="bz-chat-from">{m.fromName}</span>
              <span className="bz-chat-time">{msgTime(m.ts)}</span>
            </div>
            <div className="bz-chat-text">{m.text}</div>
          </div>
        ))}
      </div>
      <form className="bz-mchat-compose" onSubmit={send}>
        <input
          className="bz-mchat-input"
          type="text"
          value={draft}
          onChange={onDraft}
          placeholder="Message everyone"
          maxLength={4000}
          aria-label="Message everyone"
        />
        <button type="submit" className="bz-chat-send" title="Send" aria-label="Send">
          <IconSend size={15} />
        </button>
      </form>
    </div>
  );
});
